import { FolderOutlined, HomeOutlined, ReadOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { Avatar, Button, Dropdown, Flex, Grid, Layout, Typography } from 'antd';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/ChevronDown.svg';
// Temporary: keep the primes table icon import disabled with its mobile menu entry.
// import { ReactComponent as EuroIcon } from 'assets/icons/EuroBlack.svg';
import { ReactComponent as PersonCircleIcon } from 'assets/icons/PersonCircle.svg';
import { ReactComponent as ProfileIcon } from 'assets/icons/Profile.svg';
import { ReactComponent as HellioLogo } from 'assets/logo/logo-blue.svg';
import { ReactComponent as AkeaLogo } from 'assets/logo/akea-energies.svg';
import NewDossierButton from 'components/NewDossierButton';
import SideMenu from 'components/SideMenu';
import { useAuthContext } from 'contexts/AuthContext';
import { NotificationsPanelProvider } from 'contexts/NotificationsPanelContext';
import { ContactPopover } from 'layouts/Component/ContactPopover';
import MobileMenu, {
  MobileMenuItem,
  MobileMenuTrigger,
} from 'layouts/Component/MobileMenu';
import { NotificationPopover } from 'layouts/Component/NotificationPopover';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Temporary: AccessType was used by the hidden primes table mobile menu entry.
// import { AccessType } from 'types/User';

const { Header, Content } = Layout;
const { useBreakpoint } = Grid;

type MobileMenuItemLocal = MobileMenuItem;

const AppLayout = (props: { children: React.ReactNode }) => {
  const { children } = props;
  const { auth, logout, owner, ownerIsLoading } = useAuthContext();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sm } = useBreakpoint();
  const isDesktop = !!sm;
  const [collapsed, setCollapsed] = React.useState(false);

  const displayName = `${auth?.user?.firstname} ${auth?.user?.lastname}`;
  const isImpersonated = !!auth?.user?.impersonated;

  const handleLogout = useCallback(() => {
    logout();
    navigate('/connexion');
  }, [navigate, logout]);

  const handleMenuClick = useCallback<NonNullable<MenuProps['onClick']>>(
    (info) => {
      if (info?.key === 'logout') {
        handleLogout();
      } else if (typeof info?.key === 'string') {
        navigate(info?.key);
      }
    },
    [navigate, handleLogout],
  );

  const userMenuItems = useMemo(
    () => [
      {
        key: '/mon-compte',
        label: t('global:my_account'),
      },
      {
        key: 'logout',
        label: t('global:logout'),
      },
    ],
    [t],
  );

  const mobileMenuItems = useMemo(() => {
    const items: MobileMenuItemLocal[] = [
      {
        key: '/',
        icon: HomeOutlined,
        label: t('menu:home'),
      },
      {
        key: '/dossiers',
        icon: FolderOutlined,
        label: t('menu:dossiers'),
      },
      {
        key: '/ressources',
        icon: ReadOutlined,
        label: t('menu:ressources'),
      },
    ];

    // Temporary: hide the primes table entry from the mobile menu.
    // if (auth?.user?.accessType === AccessType.CONCESSIONNAIRE) {
    //   items.push({
    //     key: '/tableau-des-primes',
    //     icon: EuroIcon,
    //     label: t('primes_table:title'),
    //   });
    // }

    items.push({
      key: '/mon-compte',
      icon: ProfileIcon,
      label: t('global:my_account'),
    });

    return items;
  }, [t]);

  return (
    <NotificationsPanelProvider>
      <MobileMenu
        menuItems={mobileMenuItems}
        owner={owner}
        ownerIsLoading={ownerIsLoading ?? false}
        isImpersonated={isImpersonated}
        onLogout={handleLogout}
      >
        <Layout className="min-h-[100dvh] print:bg-white">
          {isImpersonated && (
            <div className="print:hidden z-[1000] flex h-[52px] items-center justify-center gap-6 bg-gray-800">
              <Typography.Paragraph className="!m-0 text-white">
                {t('login:impersonate', {
                  name: displayName,
                })}
              </Typography.Paragraph>
              <Button onClick={handleLogout} type="primary">
                {t('global:logout')}
              </Button>
            </div>
          )}
          <Header className="print:hidden sticky top-0 z-[1000] flex h-12 w-full items-center justify-between border-b border-gray-200 bg-white px-3 shadow-none sm:h-16 sm:border-b-0 sm:px-4 sm:shadow-sm">
            {isDesktop ? (
              <>
                <div className="hidden h-10 items-center gap-[18px] sm:flex">
                  <HellioLogo
                    role="img"
                    aria-label="Hellio"
                    className="h-6 w-[78px]"
                  />
                  <AkeaLogo
                    role="img"
                    aria-label="Akéa Énergies"
                    className="h-9 w-[77px]"
                  />
                </div>

                <div className="hidden items-center gap-3 sm:flex">
                  <ContactPopover />

                  <NewDossierButton />

                  <Dropdown
                    menu={{
                      items: userMenuItems,
                      onClick: handleMenuClick,
                    }}
                    placement="bottomRight"
                  >
                    <Button
                      type="text"
                      size="large"
                      className="flex items-center gap-2 hover:!bg-white"
                    >
                      <Avatar className="!flex items-center justify-center">
                        <PersonCircleIcon className="text-black" />
                      </Avatar>
                      <ChevronDownIcon className="text-bleu-hellio" />
                    </Button>
                  </Dropdown>

                  <NotificationPopover />
                </div>
              </>
            ) : (
              <div className="grid w-full grid-cols-[40px_1fr_40px] items-center">
                <MobileMenuTrigger />

                <div className="flex h-6 items-center justify-center gap-4">
                  <HellioLogo
                    role="img"
                    aria-label="Hellio"
                    className="h-4 w-[52px]"
                  />
                  <AkeaLogo
                    role="img"
                    aria-label="Akéa Énergies"
                    className="h-6 w-[51px]"
                  />
                </div>

                <div className="flex justify-end">
                  <NotificationPopover />
                </div>
              </div>
            )}
          </Header>

          <Layout>
            {isDesktop && (
              <SideMenu
                collapsed={collapsed}
                setCollapsed={setCollapsed}
                isImpersonated={isImpersonated}
              />
            )}
            <Content className="relative flex px-3 py-4 print:mt-0 print:p-0 sm:px-4">
              <Flex vertical className="grow w-full mx-auto">
                {children}
              </Flex>
            </Content>
          </Layout>
        </Layout>
      </MobileMenu>
    </NotificationsPanelProvider>
  );
};

export default React.memo(AppLayout);
